import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
    isEditOrRelistPage,
    isRelistPage,
    isStepEnabled,
    StepModel,
    updateSteps
} from '@classifieds-sell/shared';
import { combineLatest, from, Subject } from 'rxjs';
import { filter, first, skip, takeUntil } from 'rxjs/operators';
import { MOTORS_DEFAULT_STEP_STRUCTURE } from '../../constants/motors-step';
import { StepIds } from '../../models/motors-step/motors-step-model';
import {
    MotorsDraftFacade,
    MotorsDraftState,
    RouterFacade,
    UserFacade
} from '@classifieds-sell/state';
import { CategoryEnum, UserDetails } from '@classifieds-sell/datalayer';
import { routerParas } from '@classifieds-sell/shared';

@Component({
    selector: 'motors-landing',
    templateUrl: './motors-landing.component.html',
    styleUrls: ['./motors-landing.component.scss']
})
export class MotorsLandingComponent implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    private _userLoaded$: Subject<void> = new Subject<void>();
    private _draftLoaded$: Subject<void> = new Subject<void>();

    private _init = false;

    // true if initial url was '.../1234/Sub'
    // false (default) if initial url was '.../1234/Sub/<stepId>'
    private _redirectedFromRoute = false;
    // true if there is invalid step or can be landed on currentStepId
    private _redirectedFromLanding = false;

    private _draftState: MotorsDraftState;
    private _userDetails: UserDetails;

    allSet = false;

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _changeDetectorRef: ChangeDetectorRef,
        private _routerFacade: RouterFacade,
        private _draftFacade: MotorsDraftFacade,
        private _userFacade: UserFacade
    ) {
        this.initialise();
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
    }

    ngOnInit(): void {
        if (this.isWildcardPath()) {
            this.allSet = true;
            this._changeDetectorRef.markForCheck();
        }

        if (!this._init && !this.isWildcardPath()) {
            this._init = true;

            this._userFacade.init();

            this._draftFacade.init(
                this.getCategoryId(),
                this.getListingId()
            );
        }

        this._router.events
            .pipe(filter((e) => e instanceof NavigationEnd))
            .subscribe({
                next: () => {
                    if (this._redirectedFromLanding) {
                        this.allSet = true;
                        this._changeDetectorRef.markForCheck();
                    }
                }
            });

        from(this._userFacade.userDetails$)
            .pipe(takeUntil(this._destroyed$), skip(1), first())
            .subscribe((ud) => {
                this._userDetails = ud;
                this._userLoaded$.next();
            });

        from(this._draftFacade.state$)
            .pipe(takeUntil(this._destroyed$), skip(1), first())
            .subscribe((ds) => {
                if (!ds.isLoadingDraftSuccess) {
                    window.location.href = document.baseURI;
                    return;
                } else if (isEditOrRelistPage(this._activatedRoute, true)) {
                    this.validateListing(ds);
                }

                this._draftState = ds;
                this._draftLoaded$.next();
            });

        combineLatest([this._userLoaded$, this._draftLoaded$])
            .pipe(takeUntil(this._destroyed$), first())
            .subscribe(() => {
                this.prepping();
                // only run on relist, and if it was not loaded from localstorage. LocalStorage load indicates the user has made changes
                // and we should not reset the package.
                if (!this._draftState.draftLoadedFromLocalStorage && isRelistPage(this._activatedRoute, true)) {
                    this._draftFacade.resetPackageCodeIfNeeded({
                        isRelist: isRelistPage(this._activatedRoute, true)
                    });
                }
            });
    }

    // check whether ot not listing can relist or edit
    private validateListing(ds: MotorsDraftState): void {
        // Enhance later on
    }

    private initialise() {
        // this is needed to subscribe the event of initial navigation
        // which occurs by entering the url or redirected from somewhere
        this._router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                first()
            )
            .subscribe({
                next: (e) => {
                    const event = <NavigationEnd>e;
                    if (event.url !== event.urlAfterRedirects) {
                        this._redirectedFromRoute = true;
                    }
                }
            });
    }

    private getCategoryId(): CategoryEnum {
        return this._activatedRoute.firstChild.snapshot.data.categoryId;
    }

    private getListingId(): number | null {
        return routerParas(this._activatedRoute, true).listingId;
    }

    private getChildSegements(): string[] {
        const segments: string[] = [];
        let route = this._activatedRoute;
        while (route.firstChild) {
            route.firstChild.snapshot.url.forEach((u) => segments.push(u.path));
            route = route.firstChild;
        }

        return segments;
    }

    private getCurrentStepId(): StepIds {
        const segments = this.getChildSegements();
        return <StepIds>segments[segments.length - 1];
    }

    private isWildcardPath(): boolean {
        // a hacky solution, might want to look into different solution
        // because we don't want to redirect to 404 in route configuration
        // also want to keep the original url which happens to be 404
        return this._activatedRoute.firstChild.routeConfig.path === '**';
    }

    private prepping() {
        const steps = updateSteps(
            MOTORS_DEFAULT_STEP_STRUCTURE,
            this._draftState.validationResults
        );

        this._routerFacade.updateSteps(steps);
        this.redirectIfRequired(steps);
    }

    private redirectIfRequired(steps: StepModel[]) {
        const stepIdToBeLandedOn = this.findStepId(steps);

        if (stepIdToBeLandedOn !== this.getCurrentStepId()) {
            const segments = this.getChildSegements();
            segments.pop();
            segments.push(stepIdToBeLandedOn);

            setTimeout(() => {
                this._redirectedFromLanding = true;
                this._router.navigate(segments, {
                    relativeTo: this._activatedRoute,
                    queryParams: this._activatedRoute.snapshot.queryParams
                });
            }, 0);
            return;
        }

        this.allSet = true;
        this._changeDetectorRef.markForCheck();
    }

    private findStepId(steps: StepModel[]): string {
        if (this._draftState.validationResults === null) {
            return StepIds.vehicleDetails;
        }

        // find first invalid step if it exists - overrides currentStepId in state
        const firstInvalidIndex = this._draftState.validationResults.findIndex(
            (v) => v.isValid === false
        );

        const currentStepId = this.getCurrentStepId();

        if (firstInvalidIndex !== -1) {
            return this._draftState.validationResults[firstInvalidIndex].stepId;
        } else {
            if (this._redirectedFromRoute) {
                return (
                    this._draftState.draftData.currentStepId ||
                    StepIds.vehicleDetails
                );
            } else {
                if (!isStepEnabled(steps, currentStepId)) {
                    if (
                        !isStepEnabled(
                            steps,
                            this._draftState.draftData.currentStepId
                        )
                    ) {
                        return StepIds.vehicleDetails;
                    } else {
                        return (
                            this._draftState.draftData.currentStepId ||
                            StepIds.vehicleDetails
                        );
                    }
                } else {
                    return currentStepId;
                }
            }
        }
    }
}
